import type { QuoteFile } from 'paintscout'

export type CompanyCamProject = {
  id: string
  name?: string
  featuredImage?: string
  address?: {
    street_address_1?: string
    street_address_2?: string
    city?: string
    state?: string
    postal_code?: string
    country?: string
  }
}

// The selectable tabs in AddFileDialog. Defined here (rather than the component)
// so the persistence helpers can validate a stored value against the live set.
export const ADD_FILE_TABS = ['upload', 'deal-files', 'company-cam'] as const
export type AddFileDialogTab = (typeof ADD_FILE_TABS)[number]

// Narrow an unknown value (a stored string, or MUI's Tabs onChange value) to a
// real tab, so neither a drifted localStorage entry nor an unexpected event
// value can be cast blindly into corrupt state.
export function isAddFileDialogTab(value: unknown): value is AddFileDialogTab {
  return typeof value === 'string' && (ADD_FILE_TABS as readonly string[]).includes(value)
}

// The Annotated/Original/All toggle inside the CompanyCam picker. 'all' shows
// each photo's newest render (annotated if it has one, else original) — see
// toAllVariantPhotoFile.
export type CompanyCamPhotoTab = 'annotated' | 'original' | 'all'

// Same validation rationale as isAddFileDialogTab: guard a stored value so a
// drifted localStorage entry can never restore into a bad state.
export function isCompanyCamPhotoTab(value: unknown): value is CompanyCamPhotoTab {
  return value === 'annotated' || value === 'original' || value === 'all'
}

// Which tab EditIntegrationItemFilesDialog's separate, older CompanyCam picker
// opens on — that picker only ever renders Annotated/Original, so this must
// stay one of those two. AddFileDialog uses DEFAULT_ADD_FILE_PHOTO_TAB instead.
export const DEFAULT_COMPANY_CAM_PHOTO_TAB: CompanyCamPhotoTab = 'annotated'

// Which tab AddFileDialog's picker opens on when nothing is remembered for this
// user yet (see readLastPhotoTab/writeLastPhotoTab below) — issue #23039.
export const DEFAULT_ADD_FILE_PHOTO_TAB: CompanyCamPhotoTab = 'all'

// Remember the last CompanyCam project a user picked from, so re-opening the
// picker (e.g. on a different line item of the same estimate) jumps straight
// back to that project instead of forcing another search. Keyed per-user so the
// choice never leaks across accounts on a shared browser — when there is no user
// id we don't persist or restore at all.
//
// When a `scopeId` (the quote `_id`) is supplied the memory is additionally
// scoped to that quote, so a different estimate starts fresh on the general
// project search rather than auto-jumping to whatever project the last estimate
// used. Callers without a quote (deal/note flows) omit `scopeId` and keep the
// legacy per-user key.
const CC_LAST_PROJECT_KEY = 'paintscout.companycam.lastProject'

export function ccProjectStorageKey(userId: string, scopeId?: string): string {
  // `scopeId` is the quote's CouchDB `_id` (e.g. `quote_<uuid>`) and `userId` is
  // the Auth0 id without its prefix — neither contains a `.`, so the dotted key
  // segments stay unambiguous. If a future caller passes a dot-bearing id this
  // would need encoding/a different delimiter.
  const base = `${CC_LAST_PROJECT_KEY}.${userId}`
  return scopeId ? `${base}.${scopeId}` : base
}

export function readLastCcProject(userId?: string, scopeId?: string): CompanyCamProject | null {
  if (!userId) return null
  try {
    const raw = window.localStorage.getItem(ccProjectStorageKey(userId, scopeId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as CompanyCamProject
    // Guard against a drifted/malformed shape — `id` is required to fetch the
    // project's photos downstream, so anything without it is treated as absent.
    return parsed && typeof parsed.id === 'string' && parsed.id ? parsed : null
  } catch {
    return null
  }
}

export function writeLastCcProject(
  userId: string | undefined,
  scopeId: string | undefined,
  project: CompanyCamProject | null
): void {
  if (!userId) return
  try {
    const key = ccProjectStorageKey(userId, scopeId)
    if (project) window.localStorage.setItem(key, JSON.stringify(project))
    else window.localStorage.removeItem(key)
  } catch {
    // ignore storage failures (private mode / quota) — memory is a convenience
  }
}

// Remember the tab a user last selected, so re-opening the dialog lands them
// back where they were (e.g. straight onto Company Cam if that's what they
// used last). Keyed per-user like the project memory above.
const LAST_TAB_KEY = 'paintscout.addFile.lastTab'

export function lastTabStorageKey(userId: string): string {
  return `${LAST_TAB_KEY}.${userId}`
}

export function readLastTab(userId?: string): AddFileDialogTab | null {
  if (!userId) return null
  try {
    const raw = window.localStorage.getItem(lastTabStorageKey(userId))
    // Validate against the live set so a removed/renamed tab can never be
    // restored into an unavailable state.
    return isAddFileDialogTab(raw) ? raw : null
  } catch {
    return null
  }
}

export function writeLastTab(userId: string | undefined, tab: AddFileDialogTab): void {
  if (!userId) return
  try {
    window.localStorage.setItem(lastTabStorageKey(userId), tab)
  } catch {
    // ignore storage failures (private mode / quota) — memory is a convenience
  }
}

// Remember whether the user last viewed All, Annotated, or Original photos in
// the CompanyCam picker, so re-opening the picker (a new project, a re-entered
// one, or a reopened dialog) lands on the same tab. Keyed per-user like the tab
// and project memory above.
//
// This existed once before (#21265) and was reverted (ENG-1314 QA) because it
// carried a per-project choice into the next estimate. Issue #23039 asks for
// exactly that persistence back, so it is re-added unchanged in shape, just
// widened to the third 'all' value.
const PHOTO_TAB_KEY = 'paintscout.addFile.photoTab'

export function photoTabStorageKey(userId: string): string {
  return `${PHOTO_TAB_KEY}.${userId}`
}

export function readLastPhotoTab(userId?: string): CompanyCamPhotoTab | null {
  if (!userId) return null
  try {
    const raw = window.localStorage.getItem(photoTabStorageKey(userId))
    return isCompanyCamPhotoTab(raw) ? raw : null
  } catch {
    return null
  }
}

export function writeLastPhotoTab(userId: string | undefined, tab: CompanyCamPhotoTab): void {
  if (!userId) return
  try {
    window.localStorage.setItem(photoTabStorageKey(userId), tab)
  } catch {
    // ignore storage failures (private mode / quota) — memory is a convenience
  }
}

// A picker QuoteFile sourced from CompanyCam, tagged with the upstream photo id
// so selection state can be keyed/de-duped before the id is stripped on confirm.
// `_capturedAt` is likewise picker-only, stripped alongside `_photoId` on confirm.
// `_markupPrintSrc` is the downsized markup render, kept so the All tab can swap
// `providerPrintSrc` along with `src`; it is stripped on confirm too.
export type CompanyCamPhotoFile = QuoteFile & { _photoId: string; _capturedAt?: number; _markupPrintSrc?: string }

// The subset of a CompanyCam API photo the picker consumes.
export type CompanyCamApiPhoto = {
  id: string
  url?: string | null
  // Present only when the photo was actually annotated in CompanyCam — un-annotated
  // photos have no annotation render, so this is null/absent for them (#21265).
  annotation_url?: string | null
  // CompanyCam's downsized `web` / `web_annotation` renders, used only for the PDF (#23666).
  web_url?: string | null
  annotation_web_url?: string | null
  description?: string
  // When CompanyCam captured the photo. CompanyCam's list endpoints always return
  // newest-first with no sort override, so the picker re-sorts ascending by this
  // field (see sortPhotosByCapturedAtAsc) rather than importing photos backwards (#22591).
  captured_at?: number
}

// Map a CompanyCam API photo into a picker QuoteFile. CompanyCam's `description`
// is the user-authored caption, so it lands ONLY in `caption` — never in `title`.
// Defaulting `title` to the description duplicated the same text into the file
// editor's Title and Description fields (#21263); CompanyCam photos carry no
// title. The `annotated` variant prefers the annotation image, falling back to
// the original url when CompanyCam has no annotated version.
//
// `providerId`/`providerVariant` persist the upstream identity so the file can be
// re-resolved later (the Sync action, #22142). `_photoId` is the picker's own
// marker and is stripped on confirm; these two are not.
//
// `providerMarkupSrc` records the markup that existed at pick time, so a later
// Sync can tell "marked up since it was added" from "was already marked up when
// the user chose the un-annotated render" — without it, Sync offered the markup
// on every click for a photo nothing had changed about (ENG-1118).
//
// `providerPrintSrc` is the downsized render of the chosen variant, which the PDF
// render uses instead of the full-size `src` (#23666).
export function toCompanyCamPhotoFile(
  photo: CompanyCamApiPhoto,
  variant: 'original' | 'annotated'
): CompanyCamPhotoFile {
  const isAnnotated = variant === 'annotated'
  return {
    _photoId: photo.id,
    _capturedAt: photo.captured_at,
    _markupPrintSrc: markupPrintSrcOf(photo),
    key: `${photo.id}-${variant}`,
    visibility: 'visible',
    type: 'image',
    src: (isAnnotated ? photo.annotation_url ?? photo.url : photo.url) ?? undefined,
    caption: photo?.description ?? '',
    provider: 'companycam',
    providerId: photo.id,
    providerVariant: variant,
    providerMarkupSrc: photo.annotation_url ?? undefined,
    providerPrintSrc: companyCamPrintSrcFor(photo, variant)
  }
}

// The downsized render of what `variant`'s `src` shows, for `providerPrintSrc`.
// The annotated variant shows the markup when there is one, else the original
// (the same fallback `src` uses). A markup with no web render yields undefined,
// never the plain original's web render — the PDF would then drop the markup
// the estimator chose. Undefined falls back to the full-size `src`. Shared with
// the Sync path (`companycamSync.ts`) so the two can't drift apart.
export function companyCamPrintSrcFor(
  photo: CompanyCamApiPhoto,
  variant: 'original' | 'annotated'
): string | undefined {
  const printSrc = variant === 'annotated' && photo.annotation_url ? photo.annotation_web_url : photo.web_url
  return printSrc ?? undefined
}

function markupPrintSrcOf(photo: CompanyCamApiPhoto): string | undefined {
  return companyCamPrintSrcFor(photo, 'annotated')
}

// Build the two photo lists the CompanyCam picker shows from a page of results.
// Originals include every photo; the Annotated tab includes ONLY photos that
// genuinely have a CompanyCam annotation (a non-null annotation_url), so toggling
// to Annotated meaningfully narrows to the marked-up photos instead of showing
// the same set with an identical-looking image (#21265). Keys are suffixed per
// list (via toCompanyCamPhotoFile) so a photo can be independently selected on each tab.
//
// The picker now sources its Annotated list from the `get-annotated-files` event
// (which scans past page 1) rather than from this page-local split — see
// buildAnnotatedPhotoFiles. This is kept for the Originals list, whose `annotated`
// half still describes "annotated photos on this page".
export function buildCompanyCamPhotoFiles(photos: CompanyCamApiPhoto[]): {
  originals: CompanyCamPhotoFile[]
  annotated: CompanyCamPhotoFile[]
} {
  const originals = photos.map((photo) => toCompanyCamPhotoFile(photo, 'original'))
  const annotated = photos
    .filter((photo) => !!photo.annotation_url)
    .map((photo) => toCompanyCamPhotoFile(photo, 'annotated'))
  return { originals, annotated }
}

// Map photos the server already filtered down to genuinely-annotated ones.
export function buildAnnotatedPhotoFiles(photos: CompanyCamApiPhoto[]): CompanyCamPhotoFile[] {
  return (photos ?? []).map((photo) => toCompanyCamPhotoFile(photo, 'annotated'))
}

// Re-key an already-built 'original' variant file onto the All tab's own
// namespace, showing whichever variant is that photo's current, newest
// render (the markup, if the photo has one). All used to reuse the exact key
// an Annotated/Original pick would produce, so a Select-all on All showed as
// selected on those tabs too (and vice versa) — product decided each tab
// should be its own independent picking context (ENG-1554), so All now gets
// a key no other tab can produce. A side effect: the same photo can now be
// picked once via All and again via Annotated/Original, importing it twice —
// accepted per that same decision, with no dedupe warning added.
export function toAllVariantPhotoFile(file: CompanyCamPhotoFile): CompanyCamPhotoFile {
  const hasMarkup = !!file.providerMarkupSrc
  return {
    ...file,
    key: `${file._photoId}-all`,
    src: hasMarkup ? file.providerMarkupSrc : file.src,
    providerPrintSrc: hasMarkup ? file._markupPrintSrc : file.providerPrintSrc,
    providerVariant: hasMarkup ? 'annotated' : 'original'
  }
}

// Build the All tab's list from the Original tab's already-loaded photos (the
// only fetch that returns every photo in the project, not just the annotated
// ones) — so All needs no fetch, page cursor, or loading state of its own; it
// shares Original's.
export function buildAllPhotoFiles(originals: CompanyCamPhotoFile[]): CompanyCamPhotoFile[] {
  return originals.map(toAllVariantPhotoFile)
}

// The picker's three CompanyCam photo lists, so tab-select code (the visible
// grid, Select all) has one place to pick the active tab's list instead of
// repeating the same three-way switch — a fourth tab would only need updating
// here (code review on #23039).
export function getVisibleCcPhotos(args: {
  tab: CompanyCamPhotoTab
  annotated: CompanyCamPhotoFile[]
  all: CompanyCamPhotoFile[]
  original: CompanyCamPhotoFile[]
}): CompanyCamPhotoFile[] {
  const { tab, annotated, all, original } = args
  if (tab === 'annotated') return annotated
  if (tab === 'all') return all
  return original
}

// The payload of the `get-annotated-files` event: a bounded scan of a project's
// photos, narrowed to the annotated ones.
export type CompanyCamAnnotatedResult = {
  photos: CompanyCamApiPhoto[]
  // Page to resume the scan from; null once the project is exhausted.
  nextPage: number | null
  // Photos examined by THIS request only, never a running total across resumed
  // scans — a caller that wants the total has to accumulate it. 0 means the
  // project has no photos at all.
  scanned: number
}

// Normalize the raw `processEvent.result.result` for get-annotated-files. Must
// tolerate anything: an older API build (or a test mock) answers with a bare
// array, a failed event answers null, and neither should throw or be mistaken
// for "this project has no markups".
export function parseAnnotatedFilesResult(raw: unknown): CompanyCamAnnotatedResult {
  const payload = raw && !Array.isArray(raw) && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const photos = Array.isArray(payload.photos) ? (payload.photos as CompanyCamApiPhoto[]) : []
  const nextPage = typeof payload.nextPage === 'number' && payload.nextPage >= 1 ? payload.nextPage : null
  const scanned = typeof payload.scanned === 'number' && payload.scanned >= 0 ? payload.scanned : 0
  return { photos, nextPage, scanned }
}

// Sort a page of photos oldest-captured-first. PARKED, not wired up: #22591
// applied this in `appendUniquePhotoFiles` so imports landed in upload order,
// but that flipped the picker away from the newest-first order production
// still ships, so the call was removed again (ENG-1327). The helper and its
// `_capturedAt` plumbing stay so re-enabling — ascending or descending — is a
// one-line change in `appendUniquePhotoFiles`. Missing timestamps sort last
// rather than jumping to the front.
export function sortPhotosByCapturedAtAsc<T extends { _capturedAt?: number }>(photos: T[]): T[] {
  return [...photos].sort((a, b) => {
    // Not `(a._capturedAt ?? Infinity) - (b._capturedAt ?? Infinity)`: when both
    // sides are missing that's `Infinity - Infinity`, i.e. NaN, an unspecified
    // comparator result that can reorder ties instead of leaving them stable.
    if (a._capturedAt == null && b._capturedAt == null) return 0
    if (a._capturedAt == null) return 1
    if (b._capturedAt == null) return -1
    return a._capturedAt - b._capturedAt
  })
}

// Append a page of photos, dropping any key already present. CompanyCam pages by
// offset, so a photo uploaded mid-scan shifts the window and can repeat a photo
// across pages — which would otherwise duplicate React keys and make one tile's
// click toggle two selections.
// Order is CompanyCam's own — newest-captured-first — kept by simple append, to
// match what production does (ENG-1327). See `sortPhotosByCapturedAtAsc` above.
export function appendUniquePhotoFiles(
  prev: CompanyCamPhotoFile[],
  next: CompanyCamPhotoFile[]
): CompanyCamPhotoFile[] {
  const seen = new Set(prev.map((file) => file.key))
  const additions = next.filter((file) => {
    if (file.key == null || seen.has(file.key)) return false
    seen.add(file.key)
    return true
  })
  return additions.length ? [...prev, ...additions] : prev
}

// Which empty-state message the CompanyCam photo grid should show. Kept pure so
// the wording rules are unit-testable: the old copy claimed a project had no
// annotated photos when we had only looked at its first page (#21756).
export type CcEmptyStateKey = 'no-photos' | 'no-annotated' | 'no-annotated-yet' | 'no-captioned' | 'no-captioned-yet'

export function getCcEmptyStateKey(args: {
  tab: CompanyCamPhotoTab
  captionedOnly: boolean
  // Photos examined by the annotated scan (annotated tab only).
  scanned: number
  // Whether more pages remain for the active tab.
  hasMore: boolean
}): CcEmptyStateKey {
  const { tab, captionedOnly, scanned, hasMore } = args
  const isAnnotated = tab === 'annotated'

  // An annotated scan that examined nothing means the project itself is empty —
  // never blame the markups for it.
  if (isAnnotated && scanned === 0 && !hasMore) {
    return 'no-photos'
  }
  if (captionedOnly) {
    return hasMore ? 'no-captioned-yet' : 'no-captioned'
  }
  if (isAnnotated) {
    return hasMore ? 'no-annotated-yet' : 'no-annotated'
  }
  return 'no-photos'
}

// The All/Annotated/Original choice, modelled as menu items so the mobile
// dropdown can render all three options from one Menu. Desktop renders the same
// options as a Tabs row (ENG-1314 design update; All added by #23039). All is
// listed first — it's the default tab (DEFAULT_ADD_FILE_PHOTO_TAB) and the
// fastest way to grab a project's photos as they currently look.
export interface CcMenuTabItem {
  key: CompanyCamPhotoTab
  label: string
  selected: boolean
}

export function getCompanyCamTabItems(args: {
  activeTab: CompanyCamPhotoTab
  __: (text: string) => string
}): CcMenuTabItem[] {
  const { activeTab, __ } = args
  return [
    { key: 'all', label: __('All'), selected: activeTab === 'all' },
    { key: 'annotated', label: __('Annotated'), selected: activeTab === 'annotated' },
    { key: 'original', label: __('Original'), selected: activeTab === 'original' }
  ]
}

// The three states of the Select all checkbox. "partial" drives the indeterminate
// dash: some photos on this tab are picked, not all of them. The state is scoped
// to the photos the grid currently shows, so the captioned-only filter narrows it
// too — Select all only ever acts on what the user can see.
export type CcSelectAllState = 'none' | 'partial' | 'all'

export function getCcSelectAllState(args: { shownKeys: string[]; selectedKeys: Set<string> }): CcSelectAllState {
  const { shownKeys, selectedKeys } = args
  if (!shownKeys.length) return 'none'
  let picked = 0
  for (const key of shownKeys) {
    if (selectedKeys.has(key)) picked++
  }
  if (picked === 0) return 'none'
  return picked === shownKeys.length ? 'all' : 'partial'
}

// The text beside the Select all checkbox. It reports one total — the same
// number the Confirm button shows, because both read the same selection set
// (ENG-1554). Switching tabs clears that set (handleCcPhotoTabChange), so the
// total only ever reflects the active tab's picks; a photo can no longer be
// picked on two tabs at once.
//
// It used to name an "N annotated, M original" split instead. That split was
// the warning (#22821, ENG-1314 QA) that a pick on the tab the user is not
// looking at is still uploaded, invisible in the grid. Adding All gave each tab
// its own key namespace, so the split could describe only two of the three tabs
// and the footer and Confirm disagreed once All and another tab both held
// picks. Product chose the single total over a three-way breakdown (ENG-1554).
export function getCcSelectionSummary(args: {
  shown: number
  total: number
  // More pages remain on the active tab. `total` counts what we fetched, not
  // what the project holds, so "Showing 6 of 15" would claim an exhaustion we
  // have not established — the same mistake that hid marked-up photos in
  // #21756. Say "of 15 loaded" until the cursor runs out. Select all is bounded
  // the same way: it only ever picks what the grid has.
  hasMore: boolean
  // Every picked key, all tabs — pass the selection set's size, not a per-tab
  // count, so this can never drift from the Confirm label.
  selected: number
  __: (text: string, ...args: number[]) => string
}): string {
  const { shown, total, hasMore, selected, __ } = args
  if (selected === 0)
    return hasMore ? __('Showing %s of %s loaded', shown, total) : __('Showing %s of %s', shown, total)
  return __('%s selected', selected)
}

// Return the picked photos in the order their keys were selected (Set insertion
// order), dropping any keys that no longer match a loaded photo. Keeps the order
// the user clicked rather than fixed grid order.
export function orderPickedPhotos<T extends { key?: string }>(selectedKeys: Iterable<string>, photos: T[]): T[] {
  const byKey = new Map<string, T>()
  for (const photo of photos) {
    if (photo.key != null) byKey.set(photo.key, photo)
  }
  const result: T[] = []
  for (const key of selectedKeys) {
    const photo = byKey.get(key)
    if (photo) result.push(photo)
  }
  return result
}

// Strip the picker-only markers before a CompanyCam photo becomes a saved
// QuoteFile. All are internal to the picker: `_photoId` powers selection
// keying, `_capturedAt` powers the upload-order sort (#22591), `_markupPrintSrc`
// feeds the All tab's variant swap — none belongs in the saved record.
export function toConfirmedFile(file: CompanyCamPhotoFile): QuoteFile {
  const { _photoId: _id, _capturedAt: _ca, _markupPrintSrc: _mp, ...rest } = file
  return rest
}

// The analytics payload for a confirmed CompanyCam selection. Kept here, pure,
// because the point of the event is subtle enough to be worth unit-testing: a
// count of 'original' picks on its own cannot tell a deliberate choice from a
// forced one. A project whose photos carry no markup leaves the Annotated tab
// empty, so the estimator has to take the original — that is not a preference.
// `originalWithMarkupAvailable` is the number that answers the real question:
// picks where the annotated render existed and the user took the original anyway.
export type CompanyCamSelectionEvent = {
  tab: CompanyCamPhotoTab
  photoCount: number
  originalCount: number
  annotatedCount: number
  originalWithMarkupAvailable: number
  projectPhotosLoaded: number
  projectMarkupAvailableCount: number
  annotatedScanRan: boolean
}

export function buildCompanyCamSelectionEvent(args: {
  tab: CompanyCamPhotoTab
  picked: CompanyCamPhotoFile[]
  // The Original tab's loaded list — the only fetch that returns every photo in
  // the project, so it is what the "was markup even available" counts come from.
  originals: CompanyCamPhotoFile[]
  // False while the user has stayed on Original, whose flow never scans. Analysis
  // must not read a zero annotated count as "this project has no markup".
  annotatedScanRan: boolean
}): CompanyCamSelectionEvent {
  const { tab, picked, originals, annotatedScanRan } = args
  const originalPicks = picked.filter((file) => file.providerVariant !== 'annotated')
  return {
    tab,
    photoCount: picked.length,
    originalCount: originalPicks.length,
    annotatedCount: picked.length - originalPicks.length,
    originalWithMarkupAvailable: originalPicks.filter((file) => !!file.providerMarkupSrc).length,
    projectPhotosLoaded: originals.length,
    projectMarkupAvailableCount: originals.filter((file) => !!file.providerMarkupSrc).length,
    annotatedScanRan
  }
}
